import {
    BillingStatusFilter,
    BILLING_STATUS_NONE,
} from '@/components/list-page/billing-status-filter'
import { readDefaultBillingStatus } from '@/lib/billing/default-billing-status'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import {
    AppointmentDateBasis,
    DEFAULT_APPOINTMENT_DATE_BASIS,
    FinancialDashboardResponse,
    FinancialDashboardScope,
    FinancialPeriod,
    FinancialTypeFilter,
    TopRelation,
    TopRelationsQueryParams,
    getFinancialDashboard,
} from '@/lib/api/financial/financial-dashboard-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { setAccessibleCompanies } from '@/store/slices/authSlice'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdAutoAwesome, MdBusiness, MdHub, MdRefresh } from 'react-icons/md'
import { useSearchParams } from 'react-router-dom'
import AppointmentBreakdownCards from './components/appointment-breakdown-cards'
import { FinancialAssistantPanel } from './components/financial-assistant-panel'
import FinancialHelpModal, { FinancialHelpTopic } from './components/financial-help-modal'
import FinancialPeriodSelector from './components/financial-period-selector'
import FinancialSummaryCards from './components/financial-summary-cards'
import FinancialTypeToggle from './components/financial-type-toggle'
import PaymentMethodChart from './components/payment-method-chart'
import RevenueCostChart from './components/revenue-cost-chart'
import SalesSummaryTable from './components/sales-summary-table'
import StatusBreakdownGrid from './components/status-breakdown-grid'
import TopPartnersModal from './components/top-partners-modal'
import TopPartnersTable from './components/top-partners-table'

export default function FinancialDashboardPage() {
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const accessibleCompanies = useAppSelector(s => s.auth.accessibleCompanies)
    // Escopo na URL, como no registro de caixa: o link compartilhado abre na mesma visão.
    // O padrão é TODAS as filiais (decisão do owner): a diretoria olha a organização, e
    // a filial é o recorte que se escolhe. Quem acessa uma filial só soma só ela — o
    // backend une a ativa às acessíveis.
    const [searchParams, setSearchParams] = useSearchParams()
    const scope: FinancialDashboardScope =
        searchParams.get('scope') === 'company' ? 'company' : 'organization'
    const setScope = (next: FinancialDashboardScope) => {
        const params = new URLSearchParams(searchParams)
        if (next === 'company') params.set('scope', 'company')
        else params.delete('scope')
        setSearchParams(params, { replace: true })
    }
    // Permissão própria: o assistente lê bem mais que os cards desta tela.
    const { canRead: canUseAssistant } = useResourcePermissions('financial', 'ai_assistant')
    const [showAssistant, setShowAssistant] = useState(false)
    /** Explicação aberta (o "?" de cada número). `null` = fechado. */
    const [helpTopic, setHelpTopic] = useState<FinancialHelpTopic | null>(null)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [data, setData] = useState<FinancialDashboardResponse | null>(null)
    const hasDataRef = useRef(false)
    const [period, setPeriod] = useState<FinancialPeriod>('month')
    const [type, setType] = useState<FinancialTypeFilter>('ALL')
    /**
     * Status da cobrança. Vazio = todos.
     *
     * Abre no padrão escolhido nos Relatórios (a mesma preferência, guardada no
     * navegador): quem trabalha em "Pago" o dia inteiro não deve remarcar a cada tela.
     * "Sem cobrança" não vale aqui — este filtro não oferece a opção (`allowNone`).
     */
    const [billingStatus, setBillingStatus] = useState(() => {
        const padrao = readDefaultBillingStatus()
        return padrao === BILLING_STATUS_NONE ? '' : padrao
    })
    const [appointmentDateBasis, setAppointmentDateBasis] =
        useState<AppointmentDateBasis>(DEFAULT_APPOINTMENT_DATE_BASIS)
    /** Recorte de cirurgia no card de pedido/procedimento. */
    const [surgicalOnly, setSurgicalOnly] = useState(false)
    /** Card cujo "Ver todos" está aberto. `null` = modal fechado. */
    const [seeAll, setSeeAll] = useState<{
        title: string
        subtitle: string
        accentColor: string
        relation: TopRelation
        type: 'RECEIVABLE' | 'PAYABLE'
        linked?: boolean
    } | null>(null)
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(
        async (isRefresh = false) => {
            if (!user?.companyId) return
            if (period === 'custom' && (!customStart || !customEnd)) {
                setLoading(false)
                return
            }

            // Com a tela já carregada, trocar filtro só gira o "Atualizar": tirar tudo
            // para mostrar o carregamento parecia um reload da página a cada clique.
            if (isRefresh || hasDataRef.current) setRefreshing(true)
            else setLoading(true)
            setError(null)

            try {
                const response = await getFinancialDashboard({
                    period,
                    type,
                    billingStatus: billingStatus || undefined,
                    appointmentDateBasis,
                    surgicalOnly: surgicalOnly || undefined,
                    startDate: period === 'custom' ? customStart : undefined,
                    endDate: period === 'custom' ? customEnd : undefined,
                    scope,
                })
                if (response.success) {
                    hasDataRef.current = true
                    setData(response.result)
                } else {
                    setError(response.error || 'Erro ao carregar dashboard financeiro')
                }
            } catch (err) {
                setError('Erro ao carregar dashboard financeiro')
                console.error(err)
            } finally {
                setLoading(false)
                setRefreshing(false)
            }
        },
        [
            user?.companyId,
            period,
            type,
            billingStatus,
            appointmentDateBasis,
            surgicalOnly,
            customStart,
            customEnd,
            scope,
        ],
    )

    useEffect(() => {
        if (user?.companyId) load()
    }, [user?.companyId, load])

    useEffect(() => {
        if (accessibleCompanies.length === 0) {
            getAccessibleCompanies()
                .then(res => {
                    if (res.success && res.result) {
                        dispatch(setAccessibleCompanies(res.result))
                    }
                })
                .catch(() => {})
        }
    }, [accessibleCompanies.length, dispatch])

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="loading loading-spinner loading-lg text-secondary" />
                <p className="text-base-content/60 text-sm">
                    Carregando dashboard financeiro...
                </p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="text-error text-lg">Erro ao carregar dashboard</div>
                <p className="text-base-content/60 text-sm">{error}</p>
                <button className="btn btn-secondary btn-sm" onClick={() => load(true)}>
                    Tentar novamente
                </button>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-base-content/60 text-sm">Nenhum dado disponível</p>
            </div>
        )
    }

    const lastUpdated = new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(data.lastUpdated))

    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-500">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-base-content">
                        Dashboard Financeiro
                    </h1>
                    <p className="text-sm text-base-content/60">
                        {data.scope === 'organization'
                            ? `Somando ${data.companyCount} filiais · `
                            : ''}
                        Atualizado em {lastUpdated}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {accessibleCompanies.length > 1 && (
                        <div className="join">
                            <button
                                type="button"
                                className={`btn btn-sm join-item gap-1 ${scope === 'company' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                onClick={() => setScope('company')}
                            >
                                <MdBusiness className="text-base" />
                                Filial atual
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm join-item gap-1 ${scope === 'organization' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                                onClick={() => setScope('organization')}
                            >
                                <MdHub className="text-base" />
                                Todas as filiais
                            </button>
                        </div>
                    )}
                    {canUseAssistant && (
                        <button
                            onClick={() => setShowAssistant(true)}
                            className="btn btn-sm btn-primary gap-1"
                        >
                            <MdAutoAwesome className="text-base" />
                            Assistente
                        </button>
                    )}
                    <button
                        onClick={() => load(true)}
                        className="btn btn-sm btn-ghost border border-base-300"
                        disabled={refreshing}
                    >
                        <MdRefresh
                            className={`text-base ${refreshing ? 'animate-spin' : ''}`}
                        />
                        Atualizar
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap items-start justify-between gap-3">
                <FinancialPeriodSelector
                    value={period}
                    onChange={setPeriod}
                    startDate={customStart}
                    endDate={customEnd}
                    onCustomDatesChange={(s, e) => {
                        setCustomStart(s)
                        setCustomEnd(e)
                    }}
                />
                <div className="flex flex-wrap items-end gap-3">
                    <BillingStatusFilter
                        value={billingStatus}
                        onChange={setBillingStatus}
                        id="dashboard-billing-status"
                        allowNone={false}
                        className="w-44"
                    />
                    <FinancialTypeToggle value={type} onChange={setType} />
                </div>
            </div>

            {data.salesSummary && (
                <SalesSummaryTable
                    summary={data.salesSummary}
                    onHelp={() => setHelpTopic('sales')}
                />
            )}

            <FinancialSummaryCards
                stats={data.stats}
                billingStatus={billingStatus}
                type={type}
                onHelp={setHelpTopic}
            />

            <StatusBreakdownGrid
                items={data.statusBreakdown}
                onHelp={() => setHelpTopic('status')}
            />

            <RevenueCostChart
                data={data.timeSeries}
                granularity={data.granularity}
            />

            <AppointmentBreakdownCards
                items={data.appointmentBreakdown ?? []}
                dateBasis={data.appointmentDateBasis ?? appointmentDateBasis}
                onDateBasisChange={setAppointmentDateBasis}
                surgicalOnly={data.surgicalOnly ?? surgicalOnly}
                onSurgicalOnlyChange={setSurgicalOnly}
            />

            {(type === 'ALL' || type === 'RECEIVABLE') && (
                <>
                    <div className="divider">
                        <span className="text-sm text-base-content/60 font-medium">
                            Maiores Receitas
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                        <TopPartnersTable
                            title="Top Clientes"
                            subtitle="Clientes que mais geraram receita"
                            items={data.topClients ?? []}
                            accentColor="#22c55e"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Clientes',
                                    subtitle: 'Clientes que mais geraram receita',
                                    accentColor: '#22c55e',
                                    relation: 'CLIENT',
                                    type: 'RECEIVABLE',
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Parceiros (Receita)"
                            subtitle="Parceiros que mais trouxeram retorno"
                            items={data.topReceivablePartners ?? []}
                            accentColor="#10b981"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Parceiros (Receita)',
                                    subtitle: 'Parceiros que mais trouxeram retorno',
                                    accentColor: '#10b981',
                                    relation: 'PARTNER',
                                    type: 'RECEIVABLE',
                                    linked: true,
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Profissionais (Receita)"
                            subtitle="Profissionais com mais receita gerada"
                            items={data.topReceivableProfessionals ?? []}
                            accentColor="#8b5cf6"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Profissionais (Receita)',
                                    subtitle: 'Profissionais com mais receita gerada',
                                    accentColor: '#8b5cf6',
                                    relation: 'PROFESSIONAL',
                                    type: 'RECEIVABLE',
                                    linked: true,
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Hospitais (Receita)"
                            subtitle="Hospitais com maior receita"
                            items={data.topReceivableHospitals ?? []}
                            accentColor="#0ea5e9"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Hospitais (Receita)',
                                    subtitle: 'Hospitais com maior receita',
                                    accentColor: '#0ea5e9',
                                    relation: 'HOSPITAL',
                                    type: 'RECEIVABLE',
                                    linked: true,
                                })
                            }
                        />
                    </div>
                </>
            )}

            {(type === 'ALL' || type === 'PAYABLE') && (
                <>
                    <div className="divider">
                        <span className="text-sm text-base-content/60 font-medium">
                            Maiores Custos
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                        <TopPartnersTable
                            title="Top Fornecedores"
                            subtitle="Materiais OPME e insumos"
                            items={data.topSuppliers ?? []}
                            accentColor="#f97316"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Fornecedores',
                                    subtitle: 'Materiais OPME e insumos',
                                    accentColor: '#f97316',
                                    relation: 'SUPPLIER',
                                    type: 'PAYABLE',
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Parceiros (Custo)"
                            subtitle="Maiores valores a pagar a parceiros"
                            items={data.topPayablePartners ?? []}
                            accentColor="#ef4444"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Parceiros (Custo)',
                                    subtitle: 'Maiores valores a pagar a parceiros',
                                    accentColor: '#ef4444',
                                    relation: 'PARTNER',
                                    type: 'PAYABLE',
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Profissionais (Custo)"
                            subtitle="Maiores repasses a profissionais"
                            items={data.topPayableProfessionals ?? []}
                            accentColor="#a855f7"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Profissionais (Custo)',
                                    subtitle: 'Maiores repasses a profissionais',
                                    accentColor: '#a855f7',
                                    relation: 'PROFESSIONAL',
                                    type: 'PAYABLE',
                                })
                            }
                        />
                        <TopPartnersTable
                            title="Top Hospitais (Custo)"
                            subtitle="Maiores repasses hospitalares"
                            items={data.topPayableHospitals ?? []}
                            accentColor="#dc2626"
                            onSeeAll={() =>
                                setSeeAll({
                                    title: 'Top Hospitais (Custo)',
                                    subtitle: 'Maiores repasses hospitalares',
                                    accentColor: '#dc2626',
                                    relation: 'HOSPITAL',
                                    type: 'PAYABLE',
                                })
                            }
                        />
                    </div>
                </>
            )}

            <PaymentMethodChart data={data.paymentMethodBreakdown} />

            {seeAll && (
                <TopPartnersModal
                    isOpen
                    onClose={() => setSeeAll(null)}
                    title={seeAll.title}
                    subtitle={seeAll.subtitle}
                    accentColor={seeAll.accentColor}
                    query={
                        {
                            relation: seeAll.relation,
                            // O lado do caixa é o do CARD, não o do toggle: com o
                            // toggle em "Todos" a tela mostra receita e custo
                            // lado a lado, e cada card tem o seu.
                            type: seeAll.type,
                            linked: seeAll.linked,
                            period,
                            billingStatus: billingStatus || undefined,
                            startDate: period === 'custom' ? customStart : undefined,
                            endDate: period === 'custom' ? customEnd : undefined,
                            scope,
                        } satisfies Omit<
                            TopRelationsQueryParams,
                            'page' | 'pageSize'
                        >
                    }
                />
            )}

            <FinancialHelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />

            <FinancialAssistantPanel
                open={showAssistant}
                onClose={() => setShowAssistant(false)}
            />
        </div>
    )
}
